import { computed, inject, provide } from 'vue';
import { useRouter } from 'vue-router';
import {
  useApolloClient,
  useMutation,
  useQuery,
  useSubscription,
} from '@vue/apollo-composable';
import { useEventListener } from '@vueuse/core';

import {
  GetUnreadNotificationsCountDocument,
  ListNotificationsDocument,
  MarkAllNotificationsReadDocument,
  NotificationCategoryOperator,
  NotificationCreatedDocument,
  NotificationState,
  SetNotificationArchivedDocument,
  SetNotificationReadDocument,
  SnoozeNotificationDocument,
} from '@ohmybible/graphql';
import type {
  GetUnreadNotificationsCountQuery,
  ListNotificationsQuery,
  ListNotificationsQueryVariables,
  NotificationCategory,
  NotificationFieldsFragment,
} from '@ohmybible/graphql';

import type { InjectionKey } from 'vue';

export const PAGE_SIZE = 20;

// Keep a cached list's pagination meta honest when a row is optimistically added
// or removed, so `hasMore` (items.length < totalItems) and the fetchMore offset
// math (derived from items.length) both stay correct after triage.
export const adjustPageInfoTotal = <
  T extends { limit: number; totalItems: number; totalPages: number },
>(
  pageInfo: T,
  delta: number,
): T => {
  const totalItems = Math.max(0, pageInfo.totalItems + delta);

  return { ...pageInfo, totalItems, totalPages: Math.ceil(totalItems / pageInfo.limit) };
};

/**
 * A logical notifications list is identified by its triage `state`, plus an
 * optional `isRead` filter and an optional category filter. Everything — the
 * inbox tabs and every cache read/write — funnels through `buildListVariables`
 * so equal filters always produce byte-identical Apollo cache keys. (Apollo
 * canonicalises variables, so key *order* is irrelevant, but the *set* of keys
 * is not: a bare `{}` and `{ state: 'INBOX' }` are two different cache entries
 * that both "mean INBOX" — one would silently go stale. Always pass an explicit
 * `state`.)
 */
export interface NotificationListFilter {
  state: NotificationState;
  isRead?: boolean;
  categories?: NotificationCategory[];
  categoriesOperator?: NotificationCategoryOperator;
}

export const buildListVariables = (
  filter: NotificationListFilter,
  page = 1,
): ListNotificationsQueryVariables => {
  const normalized: NonNullable<ListNotificationsQueryVariables['filter']> = {
    state: filter.state,
  };

  if (filter.isRead !== undefined) {
    normalized.isRead = filter.isRead;
  }

  if (filter.categories && filter.categories.length > 0) {
    // Sorted, so picking the same two categories in either click order lands on
    // ONE cache entry rather than two that drift apart — the same rule the
    // explicit-`state` note above exists for. (Apollo canonicalises the object,
    // not the contents of an array.)
    normalized.categories = [...filter.categories].sort();
    normalized.categoriesOperator
      = filter.categoriesOperator ?? NotificationCategoryOperator.IS;
  }

  return { page, limit: PAGE_SIZE, filter: normalized };
};

// The two unfiltered INBOX lists (unread-toggle off / on). They are ALWAYS
// tracked, filter or no filter: the sidebar badge and the bell popover read them
// even when the inbox page is not mounted.
const INBOX_ALL_FILTER: NotificationListFilter = { state: NotificationState.INBOX };
const INBOX_UNREAD_FILTER: NotificationListFilter = {
  state: NotificationState.INBOX,
  isRead: false,
};

const nowIso = () => new Date().toISOString();

/**
 * INBOX == not archived AND not currently snoozed. Mirrors the server predicate
 * so the badge and list membership agree without a round-trip. ISO-8601 UTC
 * strings compare lexicographically, so a future `snoozedUntil` sorts after
 * `now` and correctly reads as "still snoozed".
 */
export const isInboxState = (notification: NotificationFieldsFragment): boolean =>
  !notification.archivedAt
  && (!notification.snoozedUntil || notification.snoozedUntil <= nowIso());

// A notification counts toward the unread badge iff it is unread AND in the
// inbox — snoozed-but-unread and archived rows are excluded, matching the
// server's `unreadNotificationsCount`.
const countsAsUnread = (notification: NotificationFieldsFragment): boolean =>
  isInboxState(notification) && !notification.isRead;

const matchesState = (
  notification: NotificationFieldsFragment,
  state: NotificationState,
): boolean => {
  switch (state) {
    case NotificationState.ARCHIVED:
      return Boolean(notification.archivedAt);
    case NotificationState.SNOOZED:
      return (
        !notification.archivedAt
        && Boolean(notification.snoozedUntil)
        && (notification.snoozedUntil ?? '') > nowIso()
      );
    default:
      return isInboxState(notification);
  }
};

/**
 * Would the server return this notification for this filter? The client-side
 * twin of `NotificationsService.getManyByUser`'s predicates, and the reason a
 * filtered list can be kept live from the cache instead of refetching on every
 * triage action and every incoming notification.
 *
 * Keep it in step with the server. In particular the `IS_NOT` branch mirrors
 * `type NOT IN (…)`: a row whose category is unknown to this build is NOT one
 * of the excluded categories, so it stays visible.
 */
export const matchesFilter = (
  notification: NotificationFieldsFragment,
  filter: NotificationListFilter,
): boolean => {
  if (!matchesState(notification, filter.state)) {
    return false;
  }

  if (filter.isRead !== undefined && notification.isRead !== filter.isRead) {
    return false;
  }

  if (!filter.categories || filter.categories.length === 0) {
    return true;
  }

  const { category } = notification;
  const selected = Boolean(category) && filter.categories.includes(category as NotificationCategory);

  return filter.categoriesOperator === NotificationCategoryOperator.IS_NOT
    ? !selected
    : selected;
};

/**
 * Single source of truth for the sidebar badge, the PWA app-icon badge and the
 * /inbox page. Instantiated once in `MainLayout` and shared with every
 * descendant via provide/inject so there is exactly one unread-count query, one
 * live subscription and one set of triage mutations regardless of how many
 * surfaces read from it.
 */
export const useNotifications = () => {
  const router = useRouter();
  const { client } = useApolloClient();

  // --- Unread count (badge source) ------------------------------------------
  // `cache-and-network` + a manual `refetchUnreadCount`: a snooze elapsing
  // produces no client event, so a pull on inbox activation / window focus is
  // the only path that re-bumps the badge for a reappearing row.
  const { result: countResult, refetch: refetchUnreadCount } = useQuery(
    GetUnreadNotificationsCountDocument,
    () => ({ fetchPolicy: 'cache-and-network' }),
  );
  const unreadCount = computed(() => countResult.value?.unreadNotificationsCount ?? 0);
  const badgeText = computed(() => (unreadCount.value > 99 ? '99+' : String(unreadCount.value)));
  // Same value, but `undefined` until the query first resolves instead of
  // collapsing to 0. The PWA app-icon badge keys off this: a cold start must not
  // read "not loaded yet" as "nothing unread" and wipe the count the service
  // worker stamped while the app was closed.
  const loadedUnreadCount = computed(() => countResult.value?.unreadNotificationsCount);

  // Triage is not broadcast between tabs — the server publishes creations, not
  // reads — so a second tab's count goes stale the moment you triage in the
  // first one. Since the app-icon badge is origin-wide, a stale tab would
  // otherwise keep stamping its outdated count over the correct one on every
  // new notification. Re-pull whenever a tab becomes visible, so the tab you are
  // actually looking at is always the one holding the truth.
  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void refetchUnreadCount();
    }
  });

  // The count is a scalar the server derives independently, so a triage action
  // does not update it automatically — nudge it in the cache and let the count
  // query's reactive result pick the change up. Clamped at 0.
  const adjustUnreadCount = (delta: number) => {
    if (delta === 0) {
      return;
    }

    client.cache.updateQuery<GetUnreadNotificationsCountQuery>(
      { query: GetUnreadNotificationsCountDocument },
      (data) => ({
        unreadNotificationsCount: Math.max(0, (data?.unreadNotificationsCount ?? 0) + delta),
      }),
    );
  };

  // Insert/remove a notification's ref in a cached list entry to match the
  // desired membership. Field values (isRead, archivedAt, …) update via Apollo
  // entity normalization from each mutation's optimisticResponse — this only
  // fixes *which* lists reference the row, which a normalized flip cannot do.
  const setListMembership = (
    variables: ListNotificationsQueryVariables,
    notification: NotificationFieldsFragment,
    present: boolean,
  ) => {
    client.cache.updateQuery<ListNotificationsQuery, ListNotificationsQueryVariables>(
      { query: ListNotificationsDocument, variables },
      (data) => {
        if (!data) {
          return;
        }

        const items = data.notifications.items;
        const has = items.some((item) => item.id === notification.id);

        // Already correct — entity fields self-update via normalization.
        if (present === has) {
          return;
        }

        if (!present) {
          return {
            ...data,
            notifications: {
              ...data.notifications,
              items: items.filter((item) => item.id !== notification.id),
              // Removing a row shrinks the server list too (state changed), so
              // decrement totalItems — otherwise the next fetchMore offset skips
              // the row that shifted into the just-vacated slot.
              pageInfo: adjustPageInfoTotal(data.notifications.pageInfo, -1),
            },
          };
        }

        // ISO timestamps compare lexicographically — insert by recency.
        const next = [...items];
        const index = next.findIndex((item) => item.createdAt < notification.createdAt);
        next.splice(index === -1 ? next.length : index, 0, notification);

        return {
          ...data,
          notifications: {
            ...data.notifications,
            items: next,
            pageInfo: adjustPageInfoTotal(data.notifications.pageInfo, 1),
          },
        };
      },
    );
  };

  /**
   * Every list currently backed by a cache entry worth keeping honest: the two
   * unfiltered INBOX ones (always), plus whatever the inbox page is showing
   * right now. Reconciliation walks this map instead of a hardcoded pair —
   * otherwise a filtered list would be the one entry nobody maintains, and it
   * would silently miss incoming notifications and keep archived rows.
   */
  const trackedLists = new Map<string, NotificationListFilter>();
  const trackKey = (filter: NotificationListFilter): string =>
    JSON.stringify(buildListVariables(filter).filter);

  trackedLists.set(trackKey(INBOX_ALL_FILTER), INBOX_ALL_FILTER);
  trackedLists.set(trackKey(INBOX_UNREAD_FILTER), INBOX_UNREAD_FILTER);

  /** Track a list for as long as it is on screen; call the result to stop. */
  const trackList = (filter: NotificationListFilter): (() => void) => {
    const key = trackKey(filter);
    trackedLists.set(key, filter);

    return () => {
      // The two unfiltered INBOX lists outlive any single surface.
      if (key !== trackKey(INBOX_ALL_FILTER) && key !== trackKey(INBOX_UNREAD_FILTER)) {
        trackedLists.delete(key);
      }
    };
  };

  // Reconcile every tracked list + the badge after a triage patch, given the
  // notification's before/after snapshots. Membership is absolute (idempotent),
  // so calling with `(after, before)` cleanly rolls an optimistic change back.
  const reconcileInbox = (
    before: NotificationFieldsFragment,
    after: NotificationFieldsFragment,
  ) => {
    adjustUnreadCount((countsAsUnread(after) ? 1 : 0) - (countsAsUnread(before) ? 1 : 0));

    for (const filter of trackedLists.values()) {
      setListMembership(buildListVariables(filter), after, matchesFilter(after, filter));
    }
  };

  // --- Live new notifications ------------------------------------------------
  // A per-instance guard against a payload being delivered twice (e.g. a ws
  // reconnect): without it the badge would double-count and the row duplicate.
  const seenIds = new Set<string>();
  const { onResult: onNotificationCreated } = useSubscription(NotificationCreatedDocument);

  onNotificationCreated((data) => {
    const notification = data?.notificationCreated?.notification;

    if (!notification || seenIds.has(notification.id)) {
      return;
    }

    seenIds.add(notification.id);
    // Purely optimistic: a per-event server refetch would race itself on rapid
    // events (Apollo dedupes the second refetch into the first's in-flight
    // request, whose stale response then overwrites the newer optimistic
    // count). The one overwrite this leaves open — an event landing while the
    // mount-time count query is still in flight — is rare and self-heals on the
    // next event or count refetch.
    adjustUnreadCount(1);

    // A freshly created notification is always unread + INBOX, so it belongs in
    // every tracked list whose filter accepts it — including a category-filtered
    // one, which is the whole point of tracking them.
    for (const filter of trackedLists.values()) {
      if (matchesFilter(notification, filter)) {
        setListMembership(buildListVariables(filter), notification, true);
      }
    }
  });

  // --- Mutations -------------------------------------------------------------
  const { mutate: setReadMutate } = useMutation(SetNotificationReadDocument);

  const setRead = async (notification: NotificationFieldsFragment, isRead: boolean) => {
    if (notification.isRead === isRead) {
      return;
    }

    const after: NotificationFieldsFragment = {
      ...notification,
      isRead,
      readAt: isRead ? (notification.readAt ?? nowIso()) : null,
    };
    reconcileInbox(notification, after);

    try {
      await setReadMutate({
        variables: { input: { id: notification.id, isRead } },
        optimisticResponse: {
          __typename: 'Mutation',
          setNotificationRead: { ...after, __typename: 'Notification' },
        },
      });
    } catch (error) {
      reconcileInbox(after, notification);
      throw error;
    }
  };

  const toggleRead = (notification: NotificationFieldsFragment) =>
    setRead(notification, !notification.isRead);

  const { mutate: setArchivedMutate } = useMutation(SetNotificationArchivedDocument);

  const setArchived = async (notification: NotificationFieldsFragment, archived: boolean) => {
    if (Boolean(notification.archivedAt) === archived) {
      return;
    }

    // Archiving marks read (isRead=true, readAt if unset); unarchiving leaves
    // read state untouched. Mirrors the server semantics for the optimistic pass.
    const after: NotificationFieldsFragment = archived
      ? {
          ...notification,
          archivedAt: nowIso(),
          isRead: true,
          readAt: notification.readAt ?? nowIso(),
        }
      : { ...notification, archivedAt: null };
    reconcileInbox(notification, after);

    try {
      await setArchivedMutate({
        variables: { input: { id: notification.id, archived } },
        optimisticResponse: {
          __typename: 'Mutation',
          setNotificationArchived: { ...after, __typename: 'Notification' },
        },
      });
    } catch (error) {
      reconcileInbox(after, notification);
      throw error;
    }
  };

  const { mutate: snoozeMutate } = useMutation(SnoozeNotificationDocument);

  // `snoozedUntil = null` unsnoozes. Snoozing never changes read state.
  const snooze = async (
    notification: NotificationFieldsFragment,
    snoozedUntil: Date | null,
  ) => {
    const iso = snoozedUntil ? snoozedUntil.toISOString() : null;
    const after: NotificationFieldsFragment = { ...notification, snoozedUntil: iso };
    reconcileInbox(notification, after);

    try {
      await snoozeMutate({
        variables: { input: { id: notification.id, snoozedUntil: iso } },
        optimisticResponse: {
          __typename: 'Mutation',
          snoozeNotification: { ...after, __typename: 'Notification' },
        },
      });
    } catch (error) {
      reconcileInbox(after, notification);
      throw error;
    }
  };

  const { mutate: markAllReadMutate, loading: marking } = useMutation(
    MarkAllNotificationsReadDocument,
  );

  // Server marks the INBOX scope read; rewrite BOTH shared INBOX cache entries
  // directly: the unread entry empties wholesale, and the all entry's rows flip
  // to read (writing items updates the normalized entities, so no stale unread
  // dots when a surface reopens later). The inbox page's list reads these exact
  // cache entries, so it re-renders from the write — no refetch needed.
  const markAllRead = async () => {
    if (unreadCount.value === 0) {
      return;
    }

    await markAllReadMutate();
    client.cache.updateQuery<GetUnreadNotificationsCountQuery>(
      { query: GetUnreadNotificationsCountDocument },
      () => ({ unreadNotificationsCount: 0 }),
    );

    // Every tracked INBOX list, not just the unfiltered pair: a category-filtered
    // unread list must empty too, or it would keep showing rows the server has
    // just marked read. SNOOZED / ARCHIVED lists are left alone — the server
    // scopes `markAllRead` to INBOX.
    for (const filter of trackedLists.values()) {
      if (filter.state !== NotificationState.INBOX) {
        continue;
      }

      const emptied = filter.isRead === false;

      client.cache.updateQuery<ListNotificationsQuery, ListNotificationsQueryVariables>(
        { query: ListNotificationsDocument, variables: buildListVariables(filter) },
        (data) => {
          if (!data) {
            return;
          }

          if (emptied) {
            return {
              ...data,
              notifications: {
                ...data.notifications,
                items: [],
                pageInfo: {
                  ...data.notifications.pageInfo,
                  totalItems: 0,
                  totalPages: 0,
                },
              },
            };
          }

          return {
            ...data,
            notifications: {
              ...data.notifications,
              items: data.notifications.items.map((item) => ({
                ...item,
                isRead: true,
                readAt: item.readAt ?? nowIso(),
              })),
            },
          };
        },
      );
    }
  };

  const openNotification = async (notification: NotificationFieldsFragment) => {
    if (!notification.isRead) {
      void setRead(notification, true);
    }

    if (notification.path) {
      await router.push(notification.path);
    }
  };

  return {
    // Badge (sidebar nav + PWA app icon)
    unreadCount,
    loadedUnreadCount,
    badgeText,
    adjustUnreadCount,
    refetchUnreadCount,
    // Keeps a surface's own list (e.g. a filtered inbox) reconciled while shown
    trackList,
    // Triage actions (consumed by the inbox page)
    marking,
    markAllRead,
    setRead,
    toggleRead,
    setArchived,
    snooze,
    openNotification,
  };
};

export type NotificationsContext = ReturnType<typeof useNotifications>;

const NOTIFICATIONS_KEY: InjectionKey<NotificationsContext> = Symbol('notifications');

/** Called once in `MainLayout` to expose the instance to descendants. */
export const provideNotifications = (context: NotificationsContext): void => {
  provide(NOTIFICATIONS_KEY, context);
};

/** Consumed by the sidebar badge and the inbox page. Throws if used outside the
 *  provider. */
export const useNotificationsContext = (): NotificationsContext => {
  const context = inject(NOTIFICATIONS_KEY);

  if (!context) {
    throw new Error('Notifications context is missing — render within <MainLayout>.');
  }

  return context;
};
